import type { Plan } from "./feature-access";

export const UNIT_WINDOW_HOURS = 3;
export const UNITS_PER_WINDOW = 3;

const WINDOW_MS = UNIT_WINDOW_HOURS * 60 * 60 * 1000;

export interface UnitUsageEvent {
    unitId: string;
    startedAt: string;
}

export interface UsageSummary {
    limited: boolean;
    limit: number | null;
    used: number;
    remaining: number | null;
    windowHours: number;
    resetAt: string | null;
    units: UnitUsageEvent[];
}

export function unitLimitForPlan(plan: Plan | null | undefined): number | null {
    return !plan || plan === "free" ? UNITS_PER_WINDOW : null;
}

export function unlimitedUsage(): UsageSummary {
    return {
        limited: false,
        limit: null,
        used: 0,
        remaining: null,
        windowHours: UNIT_WINDOW_HOURS,
        resetAt: null,
        units: [],
    };
}

export function summarizeUsage(
    events: UnitUsageEvent[],
    limit: number | null,
    now: Date = new Date()
): UsageSummary {
    const cutoff = now.getTime() - WINDOW_MS;
    const earliestByUnit = new Map<string, number>();

    for (const event of events) {
        const startedAt = new Date(event.startedAt).getTime();
        if (!Number.isFinite(startedAt) || startedAt <= cutoff) continue;
        const seen = earliestByUnit.get(event.unitId);
        if (seen === undefined || startedAt < seen) {
            earliestByUnit.set(event.unitId, startedAt);
        }
    }

    const units = [...earliestByUnit.entries()]
        .sort((a, b) => a[1] - b[1])
        .map(([unitId, startedAt]) => ({
            unitId,
            startedAt: new Date(startedAt).toISOString(),
        }));

    const oldest = units[0];

    return {
        limited: limit !== null,
        limit,
        used: units.length,
        remaining: limit === null ? null : Math.max(0, limit - units.length),
        windowHours: UNIT_WINDOW_HOURS,
        resetAt: oldest
            ? new Date(new Date(oldest.startedAt).getTime() + WINDOW_MS).toISOString()
            : null,
        units,
    };
}

export function canStartUnit(usage: UsageSummary, unitId: string): boolean {
    if (!usage.limited) return true;
    if (usage.units.some((u) => u.unitId === unitId)) return true;
    return (usage.remaining ?? 0) > 0;
}

export function formatResetIn(resetAt: string | null, now: Date = new Date()): string {
    if (!resetAt) return "";
    const ms = new Date(resetAt).getTime() - now.getTime();
    if (!Number.isFinite(ms) || ms <= 0) return "now";
    const totalMinutes = Math.ceil(ms / 60_000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours <= 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
}
